import React from 'react';
import { motion } from 'framer-motion';
import { Plane, Compass, Monitor, Glasses, Zap, Cpu } from 'lucide-react';
import { technologies } from '../mock';

const iconMap = {
  Plane,
  Compass,
  Monitor,
  Glasses
};

const Technologies = () => {
  return (
    <div className="bg-[#0A1628] min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#D4AF37] text-sm font-semibold mb-4"
          >
            Tecnologie Avanzate
          </motion.span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Strumentazione <span className="text-[#D4AF37]">All'Avanguardia</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Utilizziamo le tecnologie più avanzate del settore per garantire precisione, efficienza e risultati eccellenti in ogni progetto.
          </p>
        </motion.div>

        {/* Hero Visual */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-20 relative"
        >
          <div className="relative rounded-3xl overflow-hidden aspect-video">
            <img
              src="https://images.unsplash.com/photo-1628158088791-89567a8e84ec?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Professional Technology"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/50 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="text-center"
              >
                <Zap className="w-20 h-20 text-[#D4AF37] mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold text-white">Precisione Digitale</h2>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Technologies Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {technologies.map((tech, index) => {
            const IconComponent = iconMap[tech.icon];
            return (
              <motion.div
                key={tech.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-[#1A2B47] to-[#0A1628] rounded-2xl p-8 border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-[#D4AF37]/10"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <IconComponent className="w-7 h-7 text-[#0A1628]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">{tech.category}</h3>
                  </div>
                </div>
                <ul className="space-y-3">
                  {tech.items.map((item, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-start space-x-3 group"
                    >
                      <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0 group-hover:scale-150 transition-transform duration-300" />
                      <span className="text-gray-400 group-hover:text-gray-300 transition-colors duration-300">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Virtual Reality Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-[#1A2B47] to-[#0A1628] rounded-3xl overflow-hidden border border-[#D4AF37]/20 shadow-2xl">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Glasses className="w-8 h-8 text-[#0A1628]" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Visualizzazione in <span className="text-[#D4AF37]">Virtual Reality</span>
                </h2>
                <p className="text-lg text-gray-400 mb-6 leading-relaxed">
                  Offriamo presentazioni immersive dei progetti in Virtual Reality con Meta Quest. I clienti possono esplorare gli spazi progettati prima della realizzazione, visualizzando in scala reale ogni dettaglio.
                </p>
                <ul className="space-y-3">
                  {[
                    'Tour virtuali interattivi 360°',
                    'Visualizzazione modelli BIM in VR',
                    'Presentazioni immersive per clienti'
                  ].map((feature, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center space-x-3"
                    >
                      <div className="w-6 h-6 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Cpu className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <span className="text-gray-300">{feature}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
              <div className="relative aspect-square md:aspect-auto">
                <img
                  src="https://images.unsplash.com/photo-1618385455730-2571c38966b7"
                  alt="Virtual Reality BIM"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0A1628]/50" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Banner */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="bg-gradient-to-r from-[#D4AF37] to-[#C5A028] rounded-3xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                 linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                backgroundSize: '30px 30px'
              }} />
            </div>
            <div className="relative z-10 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-4">
                Tecnologia che fa la differenza
              </h2>
              <p className="text-lg text-[#0A1628]/80 mb-8 max-w-2xl mx-auto">
                Investiamo costantemente nelle tecnologie più innovative per offrire servizi di altissima qualità e precisione millimetrica.
              </p>
              <div className="grid grid-cols-3 gap-6">
                {[
                  { label: 'Droni Professionali', value: '3+' },
                  { label: 'Software Certificati', value: '15+' },
                  { label: 'Virtual Reality', value: 'Meta Quest' }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    className="bg-white/20 backdrop-blur-sm rounded-xl p-4"
                  >
                    <p className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-1">{stat.value}</p>
                    <p className="text-sm text-[#0A1628]/80">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Technologies;
